import React from 'react';

type MetricStatus = 'healthy' | 'warning' | 'critical' | 'gray';

interface MetricCardProps {
  title: string;
  data: any; // Using any for now, can be refined with more specific types
  type: 'system' | 'performance' | 'business' | 'ux' | 'security' | 'operational';
}

const MetricCard: React.FC<MetricCardProps> = ({ title, data, type }) => {
  if (!data) {
    return (
      <div className="metric-card loading">
        <div className="card-header">
          <h3>{title}</h3>
        </div>
        <div className="card-content">
          <div className="loading-placeholder">Loading...</div>
        </div>
      </div>
    );
  }

  const getStatusColor = (status: MetricStatus): string => {
    switch (status) {
      case 'healthy': return 'green';
      case 'warning': return 'yellow';
      case 'critical': return 'red';
      default: return 'gray';
    }
  };

  const getStatusIcon = (status: MetricStatus): string => {
    switch (status) {
      case 'healthy': return '✅';
      case 'warning': return '⚠️';
      case 'critical': return '❌';
      default: return '❓';
    }
  };

  const renderMetrics = () => {
    switch (type) {
      case 'system':
        return (
          <>
            <div className="metric">
              <span className="metric-label">Uptime</span>
              <span className="metric-value">{data.uptime?.uptime_percentage}%</span>
            </div>
            <div className="metric">
              <span className="metric-label">Memory</span>
              <span className="metric-value">{data.memory_usage?.usage_percentage}%</span>
            </div>
            <div className="metric">
              <span className="metric-label">CPU</span>
              <span className="metric-value">{data.cpu_usage?.current_usage_percentage}%</span>
            </div>
            <div className="metric">
              <span className="metric-label">Disk</span>
              <span className="metric-value">{data.disk_usage?.usage_percentage}%</span>
            </div>
          </>
        );

      case 'performance':
        return (
          <>
            <div className="metric">
              <span className="metric-label">API Response</span>
              <span className="metric-value">{data.api_performance?.avg_response_time_ms}ms</span>
            </div>
            <div className="metric">
              <span className="metric-label">Error Rate</span>
              <span className="metric-value">{data.error_rates?.error_rate_percentage}%</span>
            </div>
            <div className="metric">
              <span className="metric-label">Cache Hit</span>
              <span className="metric-value">{data.cache_performance?.hit_rate_percentage}%</span>
            </div>
            <div className="metric">
              <span className="metric-label">Queue Size</span>
              <span className="metric-value">{data.queue_performance?.queue_size}</span>
            </div>
          </>
        );

      case 'business':
        return (
          <>
            <div className="metric">
              <span className="metric-label">Active Users</span>
              <span className="metric-value">{data.active_users?.total_active_users}</span>
            </div>
            <div className="metric">
              <span className="metric-label">Bookings</span>
              <span className="metric-value">{data.booking_metrics?.total_bookings_today}</span>
            </div>
            <div className="metric">
              <span className="metric-label">Revenue</span>
              <span className="metric-value">${data.revenue_metrics?.daily_revenue}</span>
            </div>
            <div className="metric">
              <span className="metric-label">Conversion</span>
              <span className="metric-value">{data.conversion_rates?.booking_conversion_rate}%</span>
            </div>
          </>
        );

      default:
        return (
          <div className="metric">
            <span className="metric-label">Status</span>
            <span className="metric-value">{data.status}</span>
          </div>
        );
    }
  };

  return (
    <div className={`metric-card ${getStatusColor(data.status)}`}>
      <div className="card-header">
        <h3>{title}</h3>
        <span className={`status-badge ${getStatusColor(data.status)}`}>
          {getStatusIcon(data.status)} {data.status}
        </span>
      </div>
      <div className="card-content">
        <div className="metrics-list">
          {renderMetrics()}
        </div>
      </div>
    </div>
  );
};

export default MetricCard; 